import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from '../utils/api';
import { useBrand } from '../context/BrandContext';
import '../styles/DashboardHome.css';

const DEMO_STATS = {
  totalEmails: 128,
  activeReports: 42,
  scheduled: 7,
  successRate: 98,
};

const DashboardHome = () => {
  const brand = useBrand();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isDemo = Boolean(user?.demo);
  const [stats, setStats] = useState({
    totalEmails: 0,
    activeReports: 0,
    scheduled: 0,
    successRate: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const company = user?.company || 'Demo';
    document.title = isDemo ? `Dashboard – ${company}` : "Dashboard – Welcome";
    fetchDashboardStats();
  }, [isDemo]);

  const fetchDashboardStats = async () => {
    try {
      setLoading(true);
      const [reportsRes, scheduledRes] = await Promise.all([
        axios.get('/auth/reports/').catch(() => ({ data: [] })),
        axios.get('auth/scheduled_emails/').catch(() => ({ data: [] }))
      ]);

      const reports = reportsRes.data || [];
      const scheduled = scheduledRes.data || [];

      const sentEmails = reports.length;
      const scheduledCount = scheduled.length;
      const successRate = sentEmails > 0 ? 100 : 0;

      if (isDemo) {
        // Keep dashboard looking active for the group demo; prefer live counts when higher
        setStats({
          totalEmails: Math.max(sentEmails, DEMO_STATS.totalEmails),
          activeReports: Math.max(sentEmails, DEMO_STATS.activeReports),
          scheduled: Math.max(scheduledCount, DEMO_STATS.scheduled),
          successRate: DEMO_STATS.successRate,
        });
      } else {
        setStats({
          totalEmails: sentEmails,
          activeReports: sentEmails,
          scheduled: scheduledCount,
          successRate: successRate
        });
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      if (isDemo) {
        setStats(DEMO_STATS);
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: 'fa-cloud-upload-alt',
      title: 'Upload & Analyze',
      description: 'Upload files and let AI summarize and analyze their content',
      link: '/dashboard/upload',
      color: 'primary'
    },
    {
      icon: 'fa-chart-line',
      title: 'Reports',
      description: 'View all past broadcasts, search, filter, and export data',
      link: '/dashboard/reports',
      color: 'success'
    },
    {
      icon: 'fa-clock',
      title: 'Scheduled Emails',
      description: 'Manage and view scheduled email broadcasts',
      link: '/dashboard/schedule-emails',
      color: 'warning'
    },
    {
      icon: 'fa-cog',
      title: 'Settings',
      description: 'Configure your account and AI prompt settings',
      link: '/dashboard/settings',
      color: 'info'
    },
    ...(user?.role === 'master_admin'
      ? [
          {
            icon: 'fa-sync-alt',
            title: 'Subscription',
            description: 'See your plan, renewal date, and payment notices',
            link: '/dashboard/subscription',
            color: 'warning'
          },
          {
            icon: 'fa-life-ring',
            title: 'Support',
            description: 'Open a ticket with JEIC and follow the conversation',
            link: '/dashboard/support',
            color: 'danger'
          },
          {
            icon: 'fa-chart-pie',
            title: 'AI Usage',
            description: 'Monitor token usage, spend, and per-user analytics',
            link: '/dashboard/usage',
            color: 'success'
          },
          {
            icon: 'fa-users-cog',
            title: 'Manage Staff',
            description: 'Create, deactivate, and manage staff access',
            link: '/dashboard/manage-staff',
            color: 'primary'
          },
          {
            icon: 'fa-clipboard-list',
            title: 'Activity Logs',
            description: 'See what each admin and staff member did on this instance',
            link: '/dashboard/activity',
            color: 'info'
          },
        ]
      : []),
  ];

  const displayStats = [
    { label: 'Total Emails Sent', value: stats.totalEmails, icon: 'fa-envelope' },
    { label: 'Active Reports', value: stats.activeReports, icon: 'fa-file-alt' },
    { label: 'Scheduled', value: stats.scheduled, icon: 'fa-calendar' },
    { label: 'Success Rate', value: `${stats.successRate}%`, icon: 'fa-chart-pie' }
  ];

  return (
    <div className="dashboard-home">
      <div className="page-header">
        <div>
          <h1>
            {isDemo
              ? `Welcome to ${user?.company || brand.companyName}`
              : `Welcome to ${brand.productName}`}
          </h1>
          <p className="page-description">
            Analyze documents, create personalized messages, and broadcast to your audience with ease
          </p>
        </div>
      </div>

      <div className="stats-grid">
        {displayStats.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-icon">
              <i className={`fas ${stat.icon}`}></i>
            </div>
            <div className="stat-content">
              <span className="stat-value">{loading ? '...' : stat.value}</span>
              <span className="stat-label">{stat.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="features-section">
        <h2 className="section-title">Get Started</h2>
        <div className="features-grid">
          {features.map((feature, index) => (
            <Link to={feature.link} key={index} className="feature-card">
              <div className={`feature-icon feature-icon-${feature.color}`}>
                <i className={`fas ${feature.icon}`}></i>
              </div>
              <div className="feature-content">
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
              <div className="feature-arrow">
                <i className="fas fa-arrow-right"></i>
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="info-section">
        <div className="info-card">
          <div className="info-icon">
            <i className="fas fa-shield-alt"></i>
          </div>
          <div className="info-content">
            <h3>Secure & Protected</h3>
            <p>Access is restricted to authorized users only. All data is encrypted and secure.</p>
          </div>
        </div>

        <div className="info-card">
          <div className="info-icon">
            <i className="fas fa-bolt"></i>
          </div>
          <div className="info-content">
            <h3>AI-Powered</h3>
            <p>Leverage advanced AI to analyze documents and generate personalized email content automatically.</p>
          </div>
        </div>

        <div className="info-card">
          <div className="info-icon">
            <i className="fas fa-rocket"></i>
          </div>
          <div className="info-content">
            <h3>Streamlined Workflow</h3>
            <p>Upload, analyze, customize, and broadcast in minutes. Save time with our automated processes.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
